import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import UserContext from '../../contexts/UserContext';
import authenticate from '../../utils/Authenticate';
import displayError from '../../utils/ErrorHandler';

const SuperAdminHome = () => {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingSlug, setDeletingSlug] = useState(null);

  useEffect(() => {
    async function init() {
      if (!user) {
        const u = await authenticate(setUser, navigate);
        if (!u) return;
        if (u.role !== 'SuperAdmin') { navigate('/'); return; }
      } else if (user.role !== 'SuperAdmin') {
        navigate('/');
        return;
      }
      fetchCities();
    }
    init();
  }, []);

  const fetchCities = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URI}/api/superadmin/cities`, { withCredentials: true });
      if (res.data.success) setCities(res.data.data);
    } catch (err) {
      const msg = displayError(err, navigate, setUser);
      if (msg) setMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleEnterCity = async (slug) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URI}/api/superadmin/enter-city`,
        { tenant: slug },
        { withCredentials: true }
      );
      if (res.data.success) {
        const { token, tenant } = res.data.data;
        Cookies.set('accessToken', token, { expires: 1 });
        setUser({ ...user, tenant });
        navigate('/central/home');
      }
    } catch (err) {
      const msg = displayError(err, navigate, setUser);
      if (msg) setMessage(msg);
    }
  };

  const handleDeleteCity = async (slug) => {
    if (confirmDelete !== slug) {
      setConfirmDelete(slug);
      return;
    }
    setDeletingSlug(slug);
    setConfirmDelete(null);
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_API_URI}/api/superadmin/cities/${slug}`,
        { withCredentials: true }
      );
      if (res.data.success) {
        setCities((prev) => prev.filter((c) => c.slug !== slug));
      }
    } catch (err) {
      const msg = displayError(err, navigate, setUser);
      if (msg) setMessage(msg);
    } finally {
      setDeletingSlug(null);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-3 py-5">

      {/* FAB — Create City */}
      <div className="fab-container" onClick={() => navigate('/superadmin/create-city')}>
        <button className="fab-button bg-fmb">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
        <span className="fab-text text-fmb">Add City</span>
      </div>

      {message && (
        <div className="mb-4 p-3 bg-red-200 text-red-700 text-sm rounded">
          {message}
        </div>
      )}

      {loading ? (
        <div className="text-center text-gray-500 mt-8 text-sm">Loading cities...</div>
      ) : cities.length === 0 ? (
        <div className="text-center text-gray-500 mt-8">
          <p className="text-base font-medium">No cities found.</p>
          <p className="text-sm mt-1">Use the + button to create one.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 mt-2">
          {cities.map((city) => (
            <div
              key={city.slug}
              className="flex flex-row justify-between bg-slate-50 rounded-md shadow-lg p-3 border border-fmb-secondary"
            >
              {/* City info */}
              <div className="text-left flex-1 min-w-0 pr-2">
                <h3 className="font-semibold text-[16px] text-fmb capitalize truncate">
                  {city.displayName}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {city.slug}
                  {' '}&bull;{' '}
                  <span className={city.status === 'active' ? 'text-green-600' : 'text-red-500'}>
                    {city.status}
                  </span>
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col justify-center gap-1.5 shrink-0">
                <button
                  onClick={() => handleEnterCity(city.slug)}
                  className="rounded-full bg-fmb text-white text-xs font-semibold px-4 py-1.5 hover:bg-yellow-600 transition-colors"
                >
                  Enter
                </button>
                {confirmDelete === city.slug ? (
                  <>
                    <button
                      onClick={() => handleDeleteCity(city.slug)}
                      disabled={deletingSlug === city.slug}
                      className="rounded-full bg-red-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-red-700 disabled:opacity-60 transition-colors"
                    >
                      {deletingSlug === city.slug ? '...' : 'Confirm'}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(null)}
                      className="text-xs text-gray-400 hover:text-gray-600 text-center leading-none"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(city.slug)}
                    className="rounded-full border border-red-400 text-red-500 text-xs font-semibold px-4 py-1.5 hover:bg-red-50 transition-colors"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SuperAdminHome;
